/**
 * Appearance: light | dark | system, plus the brand palette.
 *
 * Both preferences live in localStorage — `s4n.theme` and `s4n.scheme` — and
 * index.html reads them before first paint so there is no flash. This provider
 * keeps `data-theme` / `data-scheme` in sync and re-resolves when the OS
 * preference changes while 'system' is selected.
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { Icon } from '../components/Icon'
import { buildMuiTheme, readTokens } from './muiTheme'

const KEY = 's4n.theme'
const SCHEME_KEY = 's4n.scheme'
const ThemeCtx = createContext(null)

/**
 * The palettes offered on the Appearance page. `dot` is the swatch shown in the
 * picker: one saturated hex per palette, so all six read the same in light and
 * dark. The accent tokens each palette actually applies live in styles.css.
 *
 * Five of the six are the printed documents' own hues — the giving panels on the back of
 * the newsletter, its masthead band, the brushstroke under its last column — carried to
 * the depth an accent needs. Emerald stays as it was: it is the default, and the app icon
 * has been emerald for five generations of artwork.
 *
 * `key` is not the colour's name and no longer tries to be. It is what sits in
 * localStorage under s4n.scheme and what index.html looks for before first paint, so
 * 'blue' addresses the turquoise palette and 'rose' the terracotta one. Renaming the keys
 * would have forgotten the palette every reader had already chosen; the mismatch is
 * spelt out here and in the brand-palettes block of styles.css and costs nothing else.
 */
export const SCHEMES = [
  { key: 'emerald', label: 'Emerald', dot: '#10b981' },
  { key: 'blue', label: 'Turquoise', dot: '#2794b0' },
  { key: 'purple', label: 'Plum', dot: '#ab3680' },
  { key: 'rose', label: 'Terracotta', dot: '#d1442e' },
  { key: 'orange', label: 'Ochre', dot: '#d1811a' },
  { key: 'slate', label: 'Slate', dot: '#64748b' },
]

const SCHEME_KEYS = SCHEMES.map((s) => s.key)

/* The palette a browser that has never chosen one gets: the first offered, which is
   also the one styles.css defines in :root rather than in a data-scheme block. The
   pre-paint script in index.html mirrors both this and the list above — change one
   and change the other. */
const DEFAULT_SCHEME = SCHEMES[0].key

const read = () => {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system'
  } catch {
    return 'system'
  }
}

const readScheme = () => {
  try {
    const v = localStorage.getItem(SCHEME_KEY)
    return SCHEME_KEYS.includes(v) ? v : DEFAULT_SCHEME
  } catch {
    return DEFAULT_SCHEME
  }
}

const systemDark = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches

/* ---------------------------------------------------------------- the tab icon
 *
 * The icon artwork is a white monogram on transparency and the colour behind it is
 * the palette's --brand, which is how the sidebar draws it (see .nav-logo). A tab
 * icon is a file rather than an element, so a stylesheet cannot reach it and the
 * palette would stop at the edge of the page. Painting the same mark over the same
 * token on a canvas keeps the tab in step.
 *
 * public/favicon-*.png still ship, painted on the default emerald: they are what the
 * browser shows before this code runs, and what it falls back to if it never does. */

/* ?v=7 for the reason index.html gives: six earlier icons have shipped under this name,
   and a cache that kept one of them would repaint the tab in artwork the app has retired. */
const MARK_SRC = '/icon-mark.png?v=7'
/* The mark occupies 96% of icon-mark.png, so this puts it at the 92% of the canvas
   that make-icons.mjs gives a favicon (INSET.rounded there) — the repainted icon and
   the file it replaces are then the same size. */
const MARK_FILL = 0.92 / 0.96

let markLoad = null
const loadMark = () => {
  if (!markLoad) {
    markLoad = new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error(`could not load ${MARK_SRC}`))
      img.src = MARK_SRC
    })
  }
  return markLoad
}

const drawIcon = (img, size, brand) => {
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  const g = c.getContext('2d')
  // Same 22% corner as the generated files. roundRect is recent enough to be worth
  // a guard; a square favicon is a far smaller loss than no favicon.
  if (g.roundRect) {
    g.beginPath()
    g.roundRect(0, 0, size, size, size * 0.22)
    g.clip()
  }
  g.fillStyle = brand
  g.fillRect(0, 0, size, size)
  g.imageSmoothingQuality = 'high'
  const d = size * MARK_FILL
  g.drawImage(img, (size - d) / 2, (size - d) / 2, d, d)
  return c.toDataURL('image/png')
}

/**
 * Repaint every <link rel="icon"> in the brand colour, each at the size it declares
 * so a 16px slot is downsampled from the artwork rather than from a bigger canvas.
 */
async function paintFavicon() {
  const brand = getComputedStyle(document.documentElement).getPropertyValue('--brand').trim()
  if (!brand) return
  const links = [...document.querySelectorAll('link[rel="icon"]')]
  if (!links.length) return
  const img = await loadMark()
  const cache = new Map()
  for (const link of links) {
    const size = Number.parseInt(link.getAttribute('sizes') ?? '', 10) || 32
    if (!cache.has(size)) cache.set(size, drawIcon(img, size, brand))
    link.href = cache.get(size)
  }
  // The address bar on mobile is the one other surface that takes the brand colour.
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.content = brand
}

export function ThemeProvider({ children }) {
  const [pref, setPref] = useState(read)
  const [scheme, setScheme] = useState(readScheme)
  const [resolved, setResolved] = useState(() => (read() === 'system' ? (systemDark() ? 'dark' : 'light') : read()))
  /* The dropdowns' MUI theme, built from the tokens styles.css resolves to. It is read
     off the document during render, which is safe because every way `resolved` or
     `scheme` changes has stamped the attribute before the render that sees it:
     index.html before the first; the pref effect below in the same pass as its
     setResolved, which only re-renders once the effect is done; the OS listener and
     chooseScheme synchronously. `scheme` is not read here, but the tokens move with it —
     the same reasoning as the chart colours in components/charts.jsx. */
  const muiTheme = useMemo(
    () => buildMuiTheme(resolved, readTokens()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolved, scheme]
  )

  useEffect(() => {
    const next = pref === 'system' ? (systemDark() ? 'dark' : 'light') : pref
    setResolved(next)
    document.documentElement.dataset.theme = next
    document.documentElement.dataset.themePref = pref
    try {
      localStorage.setItem(KEY, pref)
    } catch {}
  }, [pref])

  useEffect(() => {
    document.documentElement.dataset.scheme = scheme
    try {
      localStorage.setItem(SCHEME_KEY, scheme)
    } catch {}
    // --brand is the same in light and dark, so the palette is the only trigger.
    paintFavicon().catch(() => {})
  }, [scheme])

  useEffect(() => {
    if (pref !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      const next = mq.matches ? 'dark' : 'light'
      setResolved(next)
      document.documentElement.dataset.theme = next
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [pref])

  /* Stamp data-scheme before handing the change to React: charts read the accent
     tokens off the document with getComputedStyle during render, so a palette set
     only by the effect above would leave them a render behind. */
  const chooseScheme = useCallback((next) => {
    if (typeof document !== 'undefined') document.documentElement.dataset.scheme = next
    setScheme(next)
  }, [])

  const cycle = useCallback(() => {
    setPref((p) => (p === 'light' ? 'dark' : p === 'dark' ? 'system' : 'light'))
  }, [])

  return (
    <ThemeCtx.Provider
      value={{ pref, setPref, resolved, isDark: resolved === 'dark', cycle, scheme, setScheme: chooseScheme }}
    >
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeCtx.Provider>
  )
}

export const useTheme = () => {
  const ctx = useContext(ThemeCtx)
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider')
  return ctx
}

/** One glyph per preference, the same three the Appearance page's tiles use. */
export const MODES = [
  { key: 'light', icon: 'sun', label: 'Light' },
  { key: 'dark', icon: 'moon', label: 'Dark' },
  { key: 'system', icon: 'contrast', label: 'System' },
]

const modeOf = (pref) => MODES.find((m) => m.key === pref) ?? MODES[2]

/** "System" alone does not say which way it resolved, so the tooltip adds it. */
const describe = (pref, resolved) => {
  const { label } = modeOf(pref)
  return pref === 'system' ? `${label} (currently ${resolved})` : label
}

/**
 * The appearance control: one icon showing what is in force, cycling
 * light → dark → system on click.
 *
 * It sits top right of every page in the shell, and on the screens that stand
 * outside it — the donor sign-in, and the notice an account awaiting approval
 * gets in place of every route. Those two never had another way to turn the
 * lights down, and now the header agrees with them.
 *
 * Three clicks is the whole of it, so the palette stays on the Appearance page —
 * reached from the account menu beside this button, and setting light/dark there too.
 */
export function ThemeCycleButton() {
  const { pref, resolved, cycle } = useTheme()
  const next = MODES[(MODES.findIndex((m) => m.key === pref) + 1) % MODES.length]
  return (
    <button
      type="button"
      className="btn btn-sm btn-icon"
      onClick={cycle}
      title={`Appearance: ${describe(pref, resolved)} — switch to ${next.label.toLowerCase()}`}
      aria-label={`Appearance: ${describe(pref, resolved)}. Switch to ${next.label.toLowerCase()}`}
    >
      <Icon name={modeOf(pref).icon} size={18} />
    </button>
  )
}
